/**
 * How the JMC dashboard's activity feed is arranged.
 *
 * Raw audit rows in time order read as noise: eight rows are frequently eight
 * edits to one clinic, which pushes the one thing an operator has to notice - a
 * platform-wide change, a granted elevation, a denied access - off the panel.
 *
 * So two kinds of row. A *platform* action stands alone and is highlighted: it
 * changed something every clinic is served by, or it granted somebody reach they
 * did not have. Everything else is tenant housekeeping and is folded into one
 * row per clinic, newest first, with the count beside it.
 *
 * This lives apart from the screen because it is the part with rules in it, and
 * a rule that decides what an operator does not see is worth a test.
 */

export interface ActivityEntry {
  id: number;
  action: string;
  outcome: string;
  created_at: string;
  clinic_id: string | null;
  clinic_name: string | null;
}

export interface ActivityGroup {
  /** Stable React key: the clinic, or the single entry it stands for. */
  key: string;
  /** Highlighted, shown on its own row. */
  platform: boolean;
  clinicId: string | null;
  clinicName: string | null;
  /** Newest first. A platform group holds exactly one. */
  entries: ActivityEntry[];
  latest: string;
}

/**
 * Actions that never fold into a clinic group.
 *
 * Prefix match, because these families are whole: every `ai_model.*` is a change
 * to what generates for everyone, and every `support.elevation_*` is somebody
 * reaching into content they normally cannot read.
 */
const PLATFORM_PREFIXES = [
  'ai_model.',
  'prompt.',
  'demo.',
  'support.elevation_',
  'user.platform_role_',
  'authz.platform_denied',
];

/** A refusal is the other thing nobody should have to expand a group to find. */
export const isPlatformAction = (entry: ActivityEntry): boolean =>
  entry.outcome === 'DENIED' ||
  entry.clinic_id === null ||
  PLATFORM_PREFIXES.some((prefix) => entry.action.startsWith(prefix));

export function groupActivity(entries: ActivityEntry[], limit = 5): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  const byClinic = new Map<string, ActivityGroup>();

  // Input arrives newest first, so the first row seen for a clinic is its latest
  // and insertion order is already the order the panel wants.
  for (const entry of entries) {
    if (isPlatformAction(entry)) {
      groups.push({
        key: `e${entry.id}`,
        platform: true,
        clinicId: entry.clinic_id,
        clinicName: entry.clinic_name,
        entries: [entry],
        latest: entry.created_at,
      });
      continue;
    }
    const clinicId = entry.clinic_id!;
    const existing = byClinic.get(clinicId);
    if (existing) {
      existing.entries.push(entry);
      continue;
    }
    const group: ActivityGroup = {
      key: `c${clinicId}`,
      platform: false,
      clinicId,
      clinicName: entry.clinic_name,
      entries: [entry],
      latest: entry.created_at,
    };
    byClinic.set(clinicId, group);
    groups.push(group);
  }

  return groups.slice(0, limit);
}
